import { type AdditionalDiscountListItem, type AdditionalDiscountListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { AdditionalDiscountFormDrawer } from '../components/AdditionalDiscountFormDrawer';
import { useAdditionalDiscountsList } from '../use-additional-discounts';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const AdditionalDiscountsListPage = (): React.JSX.Element => {
  const columns: ListScreenColumns<AdditionalDiscountListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<AdditionalDiscountListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'amountPaise',
        header: 'Amount',
        sortable: true,
        width: '120px',
        cell: (row) => (
          <span className="tabular-nums">
            Rs {(Number(row.amountPaise) / 100).toLocaleString('en-IN')}
          </span>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<AdditionalDiscountListItem, AdditionalDiscountListQuery>
      title="Upsell"
      description="Fixed rupee upsell presets for itinerary pricing. Added to the grand total on the Pricing step (mutually exclusive with Discount)."
      useList={useAdditionalDiscountsList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add upsell' }}
      renderDrawer={({ open, mode, row, close }) => (
        <AdditionalDiscountFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          additionalDiscount={row}
        />
      )}
      rowActions={{ edit: true }}
      copy={{
        searchPlaceholder: 'Search by name or code (min 3 characters)',
        emptyTitle: 'No upsells match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load upsells',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
